import { useState, useEffect } from 'react';
import type { User, UpdateUserRequest, UserRole } from '@/types/user';

interface EditUserModalProps {
  user: User | null;
  onClose: () => void;
  onSubmit: (id: number, data: UpdateUserRequest) => Promise<void>;
  onChangePassword: (id: number, newPassword: string) => Promise<void>;
  isSubmitting: boolean;
}

export function EditUserModal({
  user,
  onClose,
  onSubmit,
  onChangePassword,
  isSubmitting,
}: EditUserModalProps) {
  const [formData, setFormData] = useState<UpdateUserRequest>({
    firstName: '',
    lastName: '',
    role: 'Operator',
    isActive: true,
  });
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        isActive: user.isActive,
      });
      setShowPasswordSection(false);
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
    }
  }, [user]);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstName || !formData.lastName) {
      setError('First name and last name are required');
      return;
    }

    if (showPasswordSection && newPassword) {
      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    try {
      await onSubmit(user.id, formData);
      if (showPasswordSection && newPassword) {
        await onChangePassword(user.id, newPassword);
      }
    } catch {
      setError('Failed to update user');
    }
  };

  const handleClose = () => {
    setError(null);
    setShowPasswordSection(false);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />
      <div className="relative bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Edit User</h2>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500 text-red-500 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-3 py-2 rounded bg-gray-600 border border-gray-500 text-gray-400 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-400">Email cannot be changed</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                }
                className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                }
                className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, role: e.target.value as UserRole }))
              }
              className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="Operator">Operator</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
              }
              className="w-4 h-4 rounded bg-gray-700 border-gray-600"
            />
            <label htmlFor="isActive" className="text-sm text-gray-300">
              Active
            </label>
          </div>

          {/* Change Password Section */}
          <div className="border-t border-gray-700 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowPasswordSection((v) => !v);
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              {showPasswordSection ? '− Cancel password change' : '+ Change password'}
            </button>

            {showPasswordSection && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 pr-10 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                      minLength={8}
                      placeholder="Min 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 text-xs"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded transition-colors"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
